import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ViewCarouselIcon from "@mui/icons-material/ViewCarouselOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import { useEffect, useMemo, useState } from "react";
import {
  carouselIssues,
  emptyCarousel,
  type Carousel,
  type CarouselTitle,
} from "@shared/carousel";
import { AVAILABLE_LANGUAGES } from "../LanguageTabs";
import CataloguePickerDrawer from "./CataloguePickerDrawer";
import CarouselPreview from "./CarouselPreview";
import PickedTitlesList from "./PickedTitlesList";
import { useVodTitles } from "./useVodTitles";
import { carouselFindingText, saveErrorText } from "./findings";
import {
  foyerApi,
  type CarouselRow,
  type FeatureSetRow,
} from "../../http/foyer";
import OrderedSelection from "../OrderedSelection";
import { tokens } from "../../theme";

/**
 * Create / edit a carousel (BE-28). Figma `NendaPortal_1` node `707-12505`.
 *
 * ## Seed once, remount by key
 *
 * `useState(() => …)` and no prop-watching effect anywhere. The caller passes
 * `key={editing?.id ?? "new"}`, so opening a different record REMOUNTS this
 * component and the remount IS the reset. A "did the record change?" check
 * cannot tell *"opened a different one"* from *"reopened the same one"*, and
 * gets it wrong silently in both directions. See [[cms-list-and-editor]].
 *
 * ## What is deliberately absent
 *
 * - **A sort control.** `Carousel.sort` is dead on this stack — the Red Bee
 *   branch built a query string from it and the Content API branch ignores it —
 *   and BE-10 established the catalogue has no sorting at all. A control that
 *   does nothing is worse than no control. The stored value is carried through
 *   untouched so a copied row keeps it.
 * - **`recommended` and `continue_watching`.** Both are in upstream's
 *   `NON_TRANSFORMED_CAROUSEL_TYPES` and never reach a device on this stack. A
 *   copied row carrying one is editable and is reported; nobody can create a
 *   new one.
 * - **A "published" toggle.** There is no publish state on a carousel anywhere
 *   in this model. The only publish gate is `SubPage.draft`.
 */

export interface CarouselEditorDialogProps {
  open: boolean;
  /** The row being edited, or null to create. A row IS its draft. */
  initial: Carousel | null;
  saving?: boolean;
  /** The server's refusal, as it came back. */
  error?: { code?: string; message: string } | null;
  onClose: () => void;
  onSubmit: (draft: Carousel) => void;
}

/** Only the three the stack renders. See the docblock. */
const CREATABLE_TYPES: { value: string; label: string; hint: string }[] = [
  {
    value: "curated",
    label: "Manual carousel",
    hint: "You pick every title yourself, and the order you pick them in is the order on the screen.",
  },
  {
    value: "query",
    label: "Dynamic collection",
    hint: "Filled automatically from tags, type and source when a room asks for it.",
  },
  {
    value: "epg",
    label: "Schedule (EPG)",
    hint: "Shows a channel's schedule. The app draws it itself — no media is fetched.",
  },
];

const NAME_MAX = 100;

/**
 * `assetTypeFilter`, as an operator would say it.
 *
 * The field has always executed — `assetTypesOf()` sends it to the catalogue on
 * every query rail — and has never had a control, so the editor's own hint
 * ("filled automatically from tags, type and source") promised two things you
 * could not set. This is the `type` half.
 *
 * Deliberately NOT every combination: it is a free `[String]` upstream that can
 * hold `"*"` or anything else, and a control offering the power set would be
 * unreadable. A row outside this vocabulary keeps its value and says so.
 */
const KIND_VALUES: {
  value: string;
  label: string;
  types: readonly string[];
}[] = [
  { value: "any", label: "Anything", types: [] },
  { value: "channels", label: "Channels", types: ["channel"] },
  { value: "movies", label: "Films", types: ["movie"] },
  { value: "series", label: "Series", types: ["series"] },
  {
    value: "films-series",
    label: "Films & series",
    types: ["movie", "series"],
  },
];

/** Set equality, because order in `assetTypeFilter` carries no meaning. */
function sameKind(
  kind: { types: readonly string[] },
  stored: string[]
): boolean {
  if (kind.types.length !== stored.length) return false;
  const have = new Set(stored);
  return kind.types.every((t) => have.has(t));
}

function Card({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${tokens.divider}`,
        borderRadius: "14px",
        background: tokens.surface,
        p: 2.25,
        height: "100%",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{label}</Typography>
      {hint && (
        <Typography
          sx={{ color: tokens.inkFaint, fontSize: 12.5, mt: 0.25, mb: 1.5 }}
        >
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

export default function CarouselEditorDialog({
  open,
  initial,
  saving,
  error,
  onClose,
  onSubmit,
}: CarouselEditorDialogProps) {
  // Seeded once. Never synced from props — see the docblock.
  const [draft, setDraft] = useState<Carousel>(
    () => initial ?? emptyCarousel()
  );
  const [picker, setPicker] = useState(false);
  const [addingLanguage, setAddingLanguage] = useState(false);

  const set = <K extends keyof Carousel>(key: K, value: Carousel[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  // Derived over the draft in hand, by the same function the server and the
  // list row call. That is what stops a row and its editor wording the same
  // finding two different ways.
  const issues = useMemo(() => carouselIssues(draft), [draft]);

  /**
   * Resolved ONCE here, and handed to both the ordered list and the preview.
   * They read the same picks, so two independent lookups would double the reads
   * and could disagree with each other mid-flight. Only `curated` holds picks
   * at all — a query rail is filled per room and an epg rail fetches nothing.
   */
  const picks = useMemo(
    () => (draft.type === "curated" ? (draft.assetIds ?? []) : []),
    [draft.type, draft.assetIds]
  );
  const lookup = useVodTitles(picks);

  /**
   * The products a rail can point at, fetched only when one might.
   *
   * Lazy on purpose: a curated carousel — still the common case — never asks,
   * so opening the editor costs no request it does not need. Fetched once per
   * dialog rather than lifted to the page because the dialog is remounted per
   * open anyway, so "once per mount" and "once per open" are the same thing.
   */
  const [products, setProducts] = useState<FeatureSetRow[] | null>(null);
  /**
   * The catalogue, for picking the rows a page shows.
   *
   * Best-effort and read once: this dialog is already gated on
   * `canManageDistribution`, so a failure here is a network blip rather than a
   * permission, and the page body degrades to whatever it already holds.
   */
  const [pageOptions, setPageOptions] = useState<CarouselRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    // 🚨 The CALL is inside the try, not just the promise. A `.catch()` alone
    // does not fire when the method is missing entirely — which is what a
    // partial test double looks like, and it takes the whole dialog down
    // rather than costing one picker. Same trap as `CarouselPreview`'s
    // line-up read; see the design-system note.
    void (async () => {
      try {
        const r = await foyerApi.listCarousels();
        if (alive) setPageOptions(r.carousels);
      } catch {
        if (alive) setPageOptions([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  const wantsProducts = draft.type === "query";
  useEffect(() => {
    if (!wantsProducts || products !== null) return;
    let cancelled = false;
    void foyerApi
      .listFeatureSets()
      .then((res) => {
        if (!cancelled) setProducts(res.featureSets ?? []);
      })
      // An empty list is a real answer here: the select falls back to naming
      // the stored id, so a rail stays editable even when the catalogue read
      // fails. Blanking the whole dialog over it would be worse.
      .catch(() => {
        if (!cancelled) setProducts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [wantsProducts, products]);

  /**
   * CONTENT products only. A capability (Frontlayer, Care, Signage) grants no
   * packages at all, so a rail pointing at one would resolve to nothing for
   * every room forever — offering it would be offering a guaranteed blank.
   */
  const contentProducts = useMemo(
    () => (products ?? []).filter((p) => !p.capability),
    [products]
  );

  const titles = draft.title ?? [];
  const [previewLanguage, setPreviewLanguage] = useState<string | undefined>(
    () => titles[0]?.language
  );

  const usedLanguages = new Set(titles.map((t) => t.language?.toLowerCase()));
  const addableLanguages = AVAILABLE_LANGUAGES.filter(
    (l) => !usedLanguages.has(l)
  );

  const setTitle = (index: number, patch: Partial<CarouselTitle>) =>
    set(
      "title",
      titles.map((t, i) => (i === index ? { ...t, ...patch } : t))
    );

  const isCreate = initial === null;

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 4 }}>
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: tokens.rowAlt,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <ViewCarouselIcon sx={{ color: tokens.brand }} />
            </Box>
            <Stack sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {isCreate ? "Create carousel" : "Edit carousel"}
              </Typography>
              <Typography sx={{ color: tokens.inkFaint, fontSize: 13 }}>
                Basic information about the carousel.
              </Typography>
            </Stack>
            <IconButton onClick={onClose} disabled={saving} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {saveErrorText(error.code, error.message)}
            </Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 2,
            }}
          >
            <Card label="Name" hint="Name the carousel.">
              <TextField
                fullWidth
                size="small"
                placeholder="Enter a name…"
                value={draft.name ?? ""}
                onChange={(e) => set("name", e.target.value.slice(0, NAME_MAX))}
                helperText={`${(draft.name ?? "").length} / ${NAME_MAX}`}
                inputProps={{ "aria-label": "Name" }}
              />
              {/* The id is derived from the name and NEVER moves afterwards:
                  re-minting it on rename would break every room type pointing
                  at it, silently, because nothing here has referential
                  integrity. So an existing row states its id rather than
                  implying the name still controls it. */}
              {!isCreate && (
                <Typography
                  sx={{ color: tokens.inkFaint, fontSize: 12, mt: 0.5 }}
                >
                  Id: <code>{draft.id}</code> — does not change when the name
                  does.
                </Typography>
              )}
            </Card>

            <Card label="Titles" hint="The localised titles shown on screen.">
              <Stack spacing={1}>
                {titles.map((t, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <TextField
                      select
                      size="small"
                      value={t.language ?? ""}
                      onChange={(e) =>
                        setTitle(i, { language: e.target.value })
                      }
                      sx={{ width: 96 }}
                      inputProps={{ "aria-label": "Language" }}
                    >
                      {AVAILABLE_LANGUAGES.map((l) => (
                        <MenuItem key={l} value={l}>
                          {l.toUpperCase()}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Title"
                      value={t.text ?? ""}
                      onChange={(e) => setTitle(i, { text: e.target.value })}
                      inputProps={{ "aria-label": `Title ${t.language ?? ""}` }}
                    />
                    <IconButton
                      size="small"
                      aria-label="Remove title"
                      onClick={() =>
                        set(
                          "title",
                          titles.filter((_, j) => j !== i)
                        )
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                {addingLanguage ? (
                  <TextField
                    select
                    size="small"
                    label="Add language"
                    value=""
                    onChange={(e) => {
                      set("title", [
                        ...titles,
                        { language: e.target.value, text: "" },
                      ]);
                      setAddingLanguage(false);
                    }}
                  >
                    {addableLanguages.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l.toUpperCase()}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={addableLanguages.length === 0}
                    onClick={() => setAddingLanguage(true)}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add language
                  </Button>
                )}
              </Stack>
            </Card>

            <Card
              label="Display limit"
              hint="How many titles are shown before a “Show all” card is added."
            >
              <TextField
                type="number"
                size="small"
                fullWidth
                value={draft.displayLimit ?? ""}
                onChange={(e) =>
                  // `Number("")` is 0, not NaN — an empty box must stay
                  // undefined rather than storing a confident zero.
                  set(
                    "displayLimit",
                    e.target.value === "" ? undefined : Number(e.target.value)
                  )
                }
                inputProps={{ min: 1, "aria-label": "Display limit" }}
              />
            </Card>

            <Card
              label="Show unpurchased content"
              hint="Whether content the room has not bought is shown at all."
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Switch
                  checked={draft.showNonPurchasedContent ?? true}
                  onChange={(e) =>
                    set("showNonPurchasedContent", e.target.checked)
                  }
                  inputProps={{ "aria-label": "Show unpurchased content" }}
                />
                <Typography sx={{ fontSize: 13 }}>
                  Show everything, including titles the room cannot play
                </Typography>
              </Stack>
              {/* The flag on its own decides nothing, and an operator who does
                  not know that will report the switch as broken. */}
              <Typography sx={{ color: tokens.inkFaint, fontSize: 12, mt: 1 }}>
                Only applies where the location allows it too. Off here turns it
                off everywhere; on here leaves the decision to the location.
              </Typography>
            </Card>

            {/* 🚨 A rail promoting itself into the MENU.
                Ruled by Sigge (2026-08-22): content presence is what should
                trigger a page. A rail with this on IS a tab — nobody composes
                one — so a hotel that buys the content gets the tab, the way
                buying Sports already produces the Sport rail. */}
            <Card
              label="Show as a page"
              hint="Also put this carousel in the app's menu, as a page of its own."
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Switch
                  checked={draft.page?.enabled ?? false}
                  onChange={(e) =>
                    set("page", {
                      enabled: e.target.checked,
                      // The body survives switching off, so switching back on
                      // restores the page that was arranged rather than an
                      // empty one.
                      label: draft.page?.label ?? [],
                      hideFromHome: draft.page?.hideFromHome ?? false,
                      carousels: draft.page?.carousels ?? [],
                    })
                  }
                  inputProps={{ "aria-label": "Show as a page" }}
                  data-testid="carousel-page-enabled"
                />
                <Typography sx={{ fontSize: 13 }}>
                  This carousel&rsquo;s name appears in the menu
                </Typography>
              </Stack>

              {draft.page?.enabled && (
                <Stack spacing={2} sx={{ mt: 2 }}>
                  {/* The body is NOT this rail. Sigge: "Movies as a rail on
                      main menu would be a curated list of top sellers. Movies
                      in the menu will open Action, Oldies but goldies, Marvel."
                      Said on screen because an empty page draws nothing and the
                      operator would otherwise report the toggle as broken. */}
                  <OrderedSelection
                    label="Rows on this page"
                    hint="What opens when a guest picks it — usually NOT this carousel. A Movies row on the home page is top sellers; the Movies page opens the genres. Add this carousel itself if you also want its full grid."
                    value={draft.page?.carousels ?? []}
                    options={(pageOptions ?? [])
                      .filter((c) => c.id !== draft.id)
                      .map((c) => ({ id: c.id, label: c.name || c.id }))}
                    onChange={(carousels) =>
                      set("page", {
                        enabled: true,
                        label: draft.page?.label ?? [],
                        hideFromHome: draft.page?.hideFromHome ?? false,
                        carousels,
                      })
                    }
                    testId="carousel-page-carousels"
                  />

                  {(draft.page?.carousels ?? []).length === 0 && (
                    <Alert severity="warning" data-testid="carousel-page-empty">
                      This page has no rows yet, so it will not appear in the
                      menu — a tab that opens onto nothing reads as a broken
                      app. Add at least one.
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Switch
                      checked={draft.page?.hideFromHome ?? false}
                      onChange={(e) =>
                        set("page", {
                          enabled: true,
                          label: draft.page?.label ?? [],
                          hideFromHome: e.target.checked,
                          carousels: draft.page?.carousels ?? [],
                        })
                      }
                      inputProps={{ "aria-label": "Hide from home" }}
                      data-testid="carousel-page-hide-from-home"
                    />
                    <Typography sx={{ fontSize: 13 }}>
                      Hide the row from the home page, leaving only the menu
                    </Typography>
                  </Stack>
                  <Typography sx={{ color: tokens.inkFaint, fontSize: 12 }}>
                    {/* Default off, ruled by Sigge — a promoted rail keeps its
                        place unless somebody says otherwise. */}
                    Off by default: a carousel can be both a row on the home
                    page and a page in the menu.
                  </Typography>
                </Stack>
              )}
            </Card>

            <Card
              label="Carousel type"
              hint="Whether the carousel is built by hand or filled automatically."
            >
              <TextField
                select
                size="small"
                fullWidth
                value={draft.type}
                onChange={(e) => set("type", e.target.value)}
                inputProps={{ "aria-label": "Carousel type" }}
              >
                {CREATABLE_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
                {/* A copied row can carry a type this stack does not render.
                    It stays selectable so the row remains editable — but it is
                    not offered to anybody creating one. */}
                {!CREATABLE_TYPES.some((t) => t.value === draft.type) && (
                  <MenuItem value={draft.type}>
                    {draft.type} (not rendered)
                  </MenuItem>
                )}
              </TextField>
              <Typography sx={{ color: tokens.inkFaint, fontSize: 12, mt: 1 }}>
                {CREATABLE_TYPES.find((t) => t.value === draft.type)?.hint ??
                  ""}
              </Typography>
            </Card>

            {draft.type === "epg" ? (
              <Card label="Channel" hint="The schedule this carousel shows.">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Channel assetId"
                  value={draft.channelId ?? ""}
                  onChange={(e) => set("channelId", e.target.value)}
                  inputProps={{ "aria-label": "Channel" }}
                />
              </Card>
            ) : draft.type === "query" ? (
              <Card
                label="Filled from"
                hint="What this collection draws, when a room asks for it."
              >
                <Stack spacing={2}>
                  <Box>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Product"
                      value={draft.featureSetId ?? ""}
                      onChange={(e) =>
                        set("featureSetId", e.target.value || undefined)
                      }
                      inputProps={{ "aria-label": "Product" }}
                    >
                      <MenuItem value="">
                        <em>No product — filter the whole catalogue</em>
                      </MenuItem>
                      {contentProducts.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                      {/* A stored id the catalogue did not return stays
                          selected and stays visible. Silently resetting it to
                          "no product" would rewrite the rail on the next save
                          because the operator opened a dialog. */}
                      {draft.featureSetId &&
                        !contentProducts.some(
                          (p) => p.id === draft.featureSetId
                        ) && (
                          <MenuItem value={draft.featureSetId}>
                            {draft.featureSetId} (not in the catalogue)
                          </MenuItem>
                        )}
                    </TextField>
                    <Typography
                      sx={{ color: tokens.inkFaint, fontSize: 12, mt: 0.75 }}
                    >
                      {draft.featureSetId
                        ? "Every premise that buys this product gets the rail. Rooms without it see nothing, and the rail hides itself."
                        : "Without a product this rail draws from the whole catalogue, narrowed only by the fields below."}
                    </Typography>
                  </Box>

                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Kind"
                    value={
                      KIND_VALUES.find((k) =>
                        sameKind(k, draft.assetTypeFilter ?? [])
                      )?.value ?? "custom"
                    }
                    onChange={(e) => {
                      const chosen = KIND_VALUES.find(
                        (k) => k.value === e.target.value
                      );
                      if (chosen) set("assetTypeFilter", [...chosen.types]);
                    }}
                    inputProps={{ "aria-label": "Kind" }}
                  >
                    {KIND_VALUES.map((k) => (
                      <MenuItem key={k.value} value={k.value}>
                        {k.label}
                      </MenuItem>
                    ))}
                    {/* `assetTypeFilter` is a free [String] upstream and can
                        hold combinations this control cannot express — "*"
                        among them. A copied row keeps its value instead of
                        being flattened into the nearest offered option. */}
                    {!KIND_VALUES.some((k) =>
                      sameKind(k, draft.assetTypeFilter ?? [])
                    ) && (
                      <MenuItem value="custom">
                        {(draft.assetTypeFilter ?? []).join(", ") || "—"} (as
                        stored)
                      </MenuItem>
                    )}
                  </TextField>

                  <TextField
                    size="small"
                    fullWidth
                    label="Tags"
                    placeholder="tag1, tag2"
                    value={(draft.tagIds ?? []).join(", ")}
                    onChange={(e) =>
                      set(
                        "tagIds",
                        e.target.value
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean)
                      )
                    }
                    inputProps={{ "aria-label": "Tags" }}
                  />
                </Stack>
              </Card>
            ) : (
              <Card label="Media">
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      background: tokens.rowAlt,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <ImageIcon sx={{ color: tokens.inkFaint }} />
                  </Box>
                  <Stack sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
                      {picks.length === 0
                        ? "No media picked"
                        : `${picks.length} picked`}
                    </Typography>
                    <Typography sx={{ color: tokens.inkFaint, fontSize: 12 }}>
                      {picks.length === 0
                        ? "Pick the titles this carousel shows."
                        : "Drag to set the order they appear on screen."}
                    </Typography>
                  </Stack>
                  <Button variant="outlined" onClick={() => setPicker(true)}>
                    Pick media
                  </Button>
                </Stack>

                {picks.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <PickedTitlesList
                      value={picks}
                      byId={lookup.byId}
                      unresolved={lookup.unresolved}
                      onChange={(next) => set("assetIds", next)}
                    />
                  </Box>
                )}
              </Card>
            )}
          </Box>

          {issues.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 3 }}>
              {issues.map((issue, i) => (
                <Stack
                  key={`${issue.code}-${issue.subject ?? i}`}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Chip
                    size="small"
                    label="Note"
                    sx={{ height: 20, fontSize: 11 }}
                  />
                  <Typography sx={{ fontSize: 12.5, color: tokens.inkFaint }}>
                    {carouselFindingText(issue)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}

          <Stack
            direction="row"
            alignItems="baseline"
            spacing={2}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Preview
            </Typography>
            {titles.length > 1 && (
              <Stack direction="row" spacing={0.75}>
                {titles.map((t) => (
                  <Chip
                    key={t.language}
                    size="small"
                    label={(t.language ?? "").toUpperCase()}
                    onClick={() => setPreviewLanguage(t.language)}
                    color={
                      previewLanguage === t.language ? "primary" : "default"
                    }
                    variant={
                      previewLanguage === t.language ? "filled" : "outlined"
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
          <CarouselPreview
            carousel={draft}
            language={previewLanguage}
            byId={lookup.byId}
            resolving={lookup.loading}
            // The rail IS the ordering surface now — arranging a row while
            // looking at the row needs no explaining. The list above keeps the
            // two jobs it cannot do: naming every pick, and removing one.
            onReorder={(ids) => set("assetIds", ids)}
            // A dynamic rail has no pick list, so its drag writes a SPARSE
            // overlay instead: the pins somebody moved, with everything else
            // keeping its place behind them.
            onPin={(ids) => set("assetOrder", ids)}
          />
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 2, justifyContent: "space-between" }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !draft.name?.trim()}
            onClick={() => onSubmit(draft)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {picker && (
        <CataloguePickerDrawer
          // Remounted per open, so it seeds from the CURRENT picks rather than
          // whatever it held last time.
          key={(draft.assetIds ?? []).join(",")}
          open
          value={draft.assetIds ?? []}
          onClose={() => setPicker(false)}
          onConfirm={(ids) => {
            set("assetIds", ids);
            setPicker(false);
          }}
        />
      )}
    </>
  );
}
